import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { Floor } from "shared/types/Floor"
import { isProjectFloor, isProjectRoom } from "shared/types/Project"
import { Room } from "shared/types/Room"
import { SelectOption } from "shared/types/Select"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"

import { FloorTitle } from "entities/floors/ui/floor-title/FloorTitle"
import { RoomTitle } from "entities/rooms/ui/room-title/RoomTitle"

interface ParentOptionValueProps {
  value: SelectOption<string, Room | Floor> | null
}

const ParentOptionValue = React.memo(({ value }: ParentOptionValueProps) => {
  const { t } = useTranslation()

  if (!value?.data) {
    return value ? (
      <span>{value.label}</span>
    ) : (
      <span className="combobox__placeholder">{t("General.Select")}</span>
    )
  }

  if (isProjectRoom(value.data)) {
    return (
      <RoomTitle
        room={{
          label: value.data.label,
          number: value.data.number
        }}
      />
    )
  }

  if (isProjectFloor(value.data)) {
    return <FloorTitle floor={{ label: value.data.label }} />
  }

  return <span>{value.label}</span>
})

ParentOptionValue.displayName = "ParentOptionValue"

export interface ProjectChildParentInputProps {
  options: SelectOption<string, Room | Floor>[] | undefined
  readonly?: boolean
  loading?: boolean
}

export const ProjectChildParentInput = React.memo(
  ({ options, readonly, loading }: ProjectChildParentInputProps) => {
    const { t } = useTranslation()

    const renderValue = useCallback(
      (value: SelectOption<string, Room | Floor> | null) => (
        <ParentOptionValue value={value} />
      ),
      []
    )

    return (
      <Combobox.Root
        inputID="room-input"
        data-testid="room-input"
        name="parentID"
        label={t("Room.Room")}
        className="room-input"
        readonly={readonly}
        loading={loading}
        items={options || []}
      >
        <Combobox.Label />
        {!readonly && (
          <Combobox.Control>
            <Combobox.Trigger>
              <Combobox.Value>{renderValue}</Combobox.Value>
              <Combobox.Icon />
            </Combobox.Trigger>
            <Combobox.Portal>
              <Combobox.Positioner align="start" sideOffset={8}>
                <Combobox.Popup aria-label={t("Room.Room")}>
                  <Combobox.InputContainer>
                    <Combobox.Input />
                    <Icon name={IconName.SEARCH} />
                  </Combobox.InputContainer>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(option: SelectOption<string, Room | Floor>) => (
                      <Combobox.Item key={option.value} value={option}>
                        <Combobox.ItemIndicator />
                        <Combobox.ItemContent>
                          <ParentOptionValue value={option} />
                        </Combobox.ItemContent>
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Control>
        )}
        <Combobox.Readonly items={options || []} />
        <Combobox.ErrorMessage />
      </Combobox.Root>
    )
  }
)
